import React, { useState, useEffect, useCallback } from 'react';
import { getSucursalesPaginadas, deleteSucursal } from '../services/sucursalService';
import { Link, useNavigate } from 'react-router-dom';
import { PlusCircle, ArrowLeft, Search, Landmark, Edit, Trash2 } from 'lucide-react';
import { toast, Toaster } from 'react-hot-toast';
import { useAuth } from '../contexts/AuthContext';

export default function SucursalesList() {
    const [sucursales, setSucursales] = useState([]);
    const [loading, setLoading] = useState(true);
    const [page, setPage] = useState(0);
    const [pageSize, setPageSize] = useState(10);
    const [totalPages, setTotalPages] = useState(0);
    const [totalElements, setTotalElements] = useState(0);
    const [searchTerm, setSearchTerm] = useState('');

    const navigate = useNavigate();
    const { token } = useAuth();

    const cargarSucursales = useCallback(async () => {
        try {
            setLoading(true);
            const data = await getSucursalesPaginadas(page, pageSize, searchTerm);
            setSucursales(data.content);
            setTotalPages(data.totalPages);
            setTotalElements(data.totalElements);
        } catch (error) {
            toast.error('Error al cargar las sucursales');
        } finally {
            setLoading(false);
        }
    }, [token, page, pageSize]);

    useEffect(() => {
        cargarSucursales();
    }, [cargarSucursales]);

    const handleEliminar = async (id) => {
        if (window.confirm('¿Desea eliminar esta sucursal?')) {
            try {
                await deleteSucursal(id);
                toast.success('Sucursal eliminada');
                cargarSucursales();
            } catch (error) {
                toast.error(error.message);
            }
        }
    };

    return (
        <div className="max-w-6xl mx-auto bg-white shadow-sm border border-gray-200 mb-6 font-sans">
            <Toaster position="top-right" />
            <div className="bg-slate-700 p-3 flex justify-between items-center text-white">
                <div className="flex items-center gap-3">
                    <button onClick={() => navigate('/parametrizacion')} className="p-1 hover:bg-slate-600 rounded">
                        <ArrowLeft className="w-5 h-5" />
                    </button>
                    <div className="flex items-center gap-2">
                        <Landmark className="w-5 h-5 text-emerald-400" />
                        <h1 className="text-lg font-bold tracking-tight uppercase">Gestión de Sucursales</h1>
                    </div>
                </div>
                <Link to="/parametrizacion/sucursales/nuevo" className="flex items-center gap-1 px-4 py-1.5 bg-emerald-600 hover:bg-emerald-700 text-white text-xs font-bold rounded-sm">
                    <PlusCircle className="w-4 h-4" /> NUEVA SUCURSAL
                </Link>
            </div>

            <div className="overflow-x-auto">
                <table className="w-full text-left border-collapse">
                    <thead>
                        <tr className="bg-gray-50 border-b-2 border-slate-200 text-slate-600 text-[0.65rem] font-black uppercase tracking-widest">
                            <th className="px-4 py-4 w-16 text-center">ID</th>
                            <th className="px-4 py-4">Nombre</th>
                            <th className="px-4 py-4">Código (00X)</th>
                            <th className="px-4 py-4">Ciudad/Departamento</th>
                            <th className="px-4 py-4 text-center">Tipo</th>
                            <th className="px-4 py-4 text-center">Estado</th>
                            <th className="px-4 py-4 text-center w-40">Acciones</th>
                        </tr>
                    </thead>
                    <tbody className="text-sm">
                        {sucursales.map((s) => (
                            <tr key={s.id} className="border-b border-slate-100 hover:bg-emerald-50/30 transition-colors">
                                <td className="px-4 py-3 text-center font-mono text-xs text-slate-400">{s.id}</td>
                                <td className="px-4 py-3">
                                    <div className="font-bold text-slate-800 uppercase text-xs">{s.nombre}</div>
                                    <div className="text-[0.6rem] text-slate-400 uppercase">{s.direccion}</div>
                                </td>
                                <td className="px-4 py-3 font-bold text-blue-600">{s.codigoEstablecimiento}</td>
                                <td className="px-4 py-3">
                                    <div className="text-xs uppercase text-slate-700">{s.ciudad?.nombre}</div>
                                    <div className="text-[0.6rem] uppercase text-slate-400">{s.departamento?.nombre}</div>
                                </td>
                                <td className="px-4 py-3 text-center">
                                    {s.esCasaCentral ? 
                                        <span className="px-2 py-0.5 bg-amber-100 text-amber-700 text-[0.6rem] font-black rounded-sm border border-amber-200 uppercase">Casa Central</span> :
                                        <span className="px-2 py-0.5 bg-slate-100 text-slate-600 text-[0.6rem] font-black rounded-sm border border-slate-200 uppercase tracking-tighter text-[0.6rem]">Agencia</span>
                                    }
                                </td>
                                <td className="px-4 py-3 text-center">
                                    <span className={`px-2 py-0.5 ${s.activo ? 'bg-emerald-100 text-emerald-700 border-emerald-200' : 'bg-rose-100 text-rose-700 border-rose-200'} text-[0.6rem] font-black rounded-sm border uppercase`}>
                                        {s.activo ? 'ACTIVO' : 'INACTIVO'}
                                    </span>
                                </td>
                                <td className="px-4 py-3 text-center">
                                    <div className="flex justify-center gap-2">
                                        <Link to={`/parametrizacion/sucursales/${s.id}/editar`} className="text-blue-600 text-[0.65rem] font-black">EDITAR</Link>
                                        <button onClick={() => handleEliminar(s.id)} className="text-rose-500 text-[0.65rem] font-black">QUITAR</button>
                                    </div>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>

            <div className="p-3 bg-white flex justify-end gap-1 border-t border-slate-200">
                <button onClick={() => setPage(page-1)} disabled={page === 0} className="px-4 py-1.5 bg-slate-100 border text-[0.65rem] font-black disabled:opacity-30">PREV</button>
                <button onClick={() => setPage(page+1)} disabled={page >= totalPages - 1} className="px-4 py-1.5 bg-slate-800 text-white text-[0.65rem] font-black disabled:opacity-30">NEXT</button>
            </div>
        </div>
    );
}
